import { Alert, AlertIcon, Box, Button, chakra, Collapse, Flex, FormControl, FormLabel, GridItem, Heading, HStack, IconButton, Image, Img, Input, InputGroup, InputRightElement, SimpleGrid, Stack, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiEye, HiEyeOff, HiUser } from "react-icons/hi";
import Loader from "react-loader-spinner";
import { getDeviceInfo, toAbsoluteUrl } from "../../helpers";
import { useAuth } from "../../helpers/configs/plugins/CookieAuthPlugin";
import { APP_NAME, APP_VERSION } from "../../helpers/flavor/app";
import { login } from "../../services/apiClient/auth";
import { Link } from "react-router-dom";
export default function Login() {
  const { t } = useTranslation();
  const { loginAuth, refetchUser } = useAuth();
  const { isOpen, onToggle } = useDisclosure();
  const [error, setError] = useState("");
  const recaptchaRef = useRef();
  const [logLoading, setLogLoading] = useState(false);

  // get device info
  const { deviceId, deviceName } = getDeviceInfo();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formProps = Object.fromEntries(formData);
    setLogLoading(true);
    let res = await login({ deviceId: deviceId, deviceName: deviceName, username: formProps.username, password: formProps.password });
    try {
      if (res?.header?.errorCode === "401" || res?.header?.statusCode === 401) {
        recaptchaRef.current.reset();
        setError(res?.header.errorText);
      }
      if (res.header.result) {
        await loginAuth({ token: res?.body?.accessToken });
        res && refetchUser();
      }
      if (!res.body.status) {
        recaptchaRef.current.reset();
        setError(res.body.message);
      }
    } catch (err) {
      setError(err.header?.errorCode === "401" ? t("login.somethingWentWrong") : t("login.incorrectUsernameOrPassword"));
    }
    setLogLoading(false);
  };

  return (
    <SimpleGrid pos="relative" columns="5" h="100vh" bg="white">
      <GridItem colSpan={{ base: 0, lg: 2 }} d={{ base: "none", lg: "block" }} bg="brand.500">
        <Flex flexDir="column" justifyContent="center" alignItems="center" h="100vh" color="white" gap="3">
          <Heading textTransform="capitalize">{APP_NAME}</Heading>
          <Text fontSize="xl">{t("label.shareYourInfo")}</Text>
          <Box h="70%" mt="5">
            <Image src={toAbsoluteUrl("/assets/banner-login.png")} alt="" objectFit="contain" h="full" />
          </Box>
        </Flex>
      </GridItem>
      <GridItem colSpan={{ base: 5, lg: 3 }}>
        <VStack w="full" h="full" bgRepeat="no-repeat" overflow="hidden" pb={{ base: "4rem", md: "6rem" }} justify="center" spacing="2rem">
          <VStack spacing="3">
            <Box h={{ base: "5rem", md: "8rem" }} bg="white" rounded="full">
              <Img h="full" src={toAbsoluteUrl("/assets/logo.png")} alt="" />
            </Box>
            <VStack spacing="0">
              <Text fontSize="3xl" fontWeight="bold">
                {t("label.welcome")}
              </Text>
              <Text fontSize="lg">{t("label.pleaseLogin")}</Text>
            </VStack>
          </VStack>
          <Box zIndex="banner" w={{ base: "80%", md: "50%" }}>
            <chakra.form onSubmit={handleSubmit}>
              <Stack spacing="4">
                <FormControl id="username">
                  <FormLabel fontWeight="medium" fontSize="20px">
                    {t("login.username")}
                  </FormLabel>
                  <InputGroup>
                    <InputRightElement h="full" mr="2">
                      <IconButton color="gray.500" _focus={{ outline: "none" }} bg="transparent !important" icon={<HiUser size="2em" />} />
                    </InputRightElement>
                    <Input bg="white" size="lg" py="25px" name="username" placeholder={t("login.usernamePlaceholder")} autoComplete="text" required />
                  </InputGroup>
                </FormControl>
                <FormControl id="password">
                  <FormLabel fontWeight="medium" fontSize="20px">
                    {t("login.password")}
                  </FormLabel>
                  <InputGroup>
                    <InputRightElement h="full" mr="2">
                      <IconButton tabIndex="-1" bg="transparent !important" color="gray.500" variant="ghost" aria-label={isOpen ? "Mask password" : "Reveal password"} icon={isOpen ? <HiEye size="1.8em" /> : <HiEyeOff size="1.8em" />} onClick={onToggle} _focus={{ outline: "none" }} colorScheme="brand.100" />
                    </InputRightElement>
                    <Input bg="white" size="lg" py="25px" name="password" type={isOpen ? "text" : "password"} autoComplete="current-password" placeholder={t("login.passwordPlaceholder")} required />
                  </InputGroup>
                </FormControl>

                {!logLoading && (
                  <Collapse in={error} animateOpacity>
                    <Alert status="error">
                      <AlertIcon />
                      {error}
                    </Alert>
                  </Collapse>
                )}

                <Button type="submit" py="25px" fontSize="20px" colorScheme="brand" isLoading={logLoading} spinner={<Loader type="ThreeDots" color="#fff" height={40} width={40} />} size="md">
                  {t("login.login")}
                </Button>
              </Stack>
            </chakra.form>
          </Box>

          <VStack opacity="0.9" spacing="0" pos="fixed" bottom="10" fontSize="md" fontWeight="bold">
            <Text>
              {t("login.poweredBy")}
              <Text as="span" ml="2" color="brand.500">
                UDAYA TECHNOLOGY Co., Ltd.
              </Text>
            </Text>
            <HStack>
              <Text>{t("version") + " " + APP_VERSION}</Text>
              <Text>|</Text>
              <Text _hover={{ textDecoration: "underline" }}>
                <Link to="/privacy-policy">Privacy Policy</Link>
              </Text>
            </HStack>
          </VStack>
        </VStack>
      </GridItem>
    </SimpleGrid>
  );
}
